import React from 'react';
import { useField } from 'formik';
import { FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';

interface Option {
  value: string | number;
  label: string;
}

interface SelectorFieldProps {
  name: string;
  label: string;
  options: Option[];
  defaultValue?: string | number | Array<string | number>;
  isViewOnly?: boolean;
  isRequired?: boolean;
  isTouched?: boolean;
  multiple?: boolean;
  isHidden?: boolean;
}

const SelectorField: React.FC<SelectorFieldProps> = ({
  name,
  label,
  options,
  isViewOnly = false,
  defaultValue,
  isRequired = false,
  isTouched = false,
  multiple = false,
  isHidden = false,
}) => {
  const [field, meta, helpers] = useField(name);

  // Initialize value once (prefer setting Formik initialValues, but this guards runtime)
  React.useEffect(() => {
    if (multiple) {
      if (!Array.isArray(field.value)) {
        helpers.setValue(Array.isArray(defaultValue) ? defaultValue : []);
      }
    } else {
      if (field.value === undefined || field.value === null || field.value === '') {
        if (defaultValue !== undefined) helpers.setValue(defaultValue as string | number);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Special-case finance fields (single-select only)
  React.useEffect(() => {
    if (!multiple) {
      const finance = new Set([
        'ppa_term_yrs_num',
        'ppa_escal_orig_term',
        'lease_term_yrs_num',
        'lease_escal_orig_term',
      ]);
      if (
        finance.has(field.name) &&
        (field.value === undefined || field.value === null || field.value === '')
      ) {
        if (defaultValue !== undefined) helpers.setValue(defaultValue as string | number);
      }
    }
  }, [field.name, field.value, helpers, multiple, defaultValue]);

  const inputLabelColor = () => {
    if (meta.touched && meta.error) return '#d42f2f';
    if (!isRequired || (defaultValue && !['ppa_term_yrs_num','ppa_escal_orig_term','lease_term_yrs_num','lease_escal_orig_term'].includes(field.name)) || isTouched || meta.touched)
      return 'black';
    return '#FF7E0B';
  };

  const currentValue: string | number | Array<string | number> =
    multiple
      ? (Array.isArray(field.value)
          ? field.value
          : Array.isArray(defaultValue) ? defaultValue : [])
      : (field.value ?? (defaultValue as string | number) ?? '');

  const handleChange = (e: SelectChangeEvent<typeof currentValue>) => {
    const v = e.target.value as unknown;

    if (multiple) {
      // v can be string (comma-joined) or an array
      const arr = typeof v === 'string' ? (v as string).split(',') : (v as Array<string | number>);
      // Map back to original option value types (string/number)
      const normalized = arr.map((x) => {
        const hit = options.find(o => String(o.value) === String(x));
        return hit ? hit.value : x;
        });
      helpers.setValue(normalized);
    } else {
      // Keep original type if option value is a number
      const hit = options.find(o => String(o.value) === String(v));
      helpers.setValue(hit ? hit.value : (v as string | number));
    }
  };

  return (
    <FormControl disabled={isViewOnly} fullWidth margin="normal" sx={{ display: isHidden ? 'none' : undefined }}>
      <InputLabel sx={{ fontWeight: 'bold', color: inputLabelColor() }} id={`${name}-label`}>
        {label}
      </InputLabel>
      <Select
        labelId={`${name}-label`}
        id={name}
        label={label}
        multiple={multiple}
        name={field.name}
        value={currentValue}
        disabled={isViewOnly}
        error={meta.touched && Boolean(meta.error)}
        onChange={handleChange}
        onBlur={() => helpers.setTouched(true)}
        renderValue={
          multiple
            ? (selected) => {
                const vals = selected as Array<string | number>;
                const labels = vals.map(v => options.find(o => o.value === v)?.label ?? String(v));

                const maxDisplay = 1; // <— show at most 1 label
                if (labels.length <= maxDisplay) return labels.join('; ');
                return `${labels.slice(0, maxDisplay).join('; ')} +${labels.length - maxDisplay} more`;
              }
            : undefined
        }
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {meta.touched && meta.error && (
        <Typography marginLeft={2} fontSize={13} color="error">
          {meta.error}
        </Typography>
      )}
    </FormControl>
  );
};

export default SelectorField;
